import { useState, useRef, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn, formatDateTime } from '@/lib/utils'
import { 
  MessageCircle, 
  X, 
  Send, 
  User, 
  Bot, 
  Loader2,
  AlertCircle,
  Hand
} from 'lucide-react'
import { useLocation } from 'react-router-dom'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  sources?: string[]
  isEscalation?: boolean
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hi! I\'m your Cloudflare Partner Services Assistant. How can I help you today?',
      timestamp: new Date()
    }
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  
  // Get current section from URL
  const section = location.pathname.split('/')[1] || 'general'

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isOpen])

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: input,
          history: messages.slice(-5).map(m => ({ role: m.role, content: m.content })),
          section,
          stream: false
        })
      })

      if (!response.ok) throw new Error('Failed to send message')

      const data = await response.json()

      if (data.type === 'escalation') {
        const escalationMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
          isEscalation: true
        }
        setMessages(prev => [...prev, escalationMessage])
      } else {
        const assistantMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
          sources: data.sources?.map((s: any) => s.source)
        }
        setMessages(prev => [...prev, assistantMessage])
      }
    } catch (error) {
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or contact support.',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const escalateToHuman = async () => {
    setIsLoading(true)
    
    try {
      const response = await fetch('/api/chat/escalate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: 'User requested human assistance',
          chatHistory: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
          section
        })
      })

      if (!response.ok) throw new Error('Failed to escalate')

      const escalationMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'I\'ve escalated your request to our Partner Services team. You\'ll receive a response within 24 hours.',
        timestamp: new Date(),
        isEscalation: true
      }
      setMessages(prev => [...prev, escalationMessage])
    } catch (error) {
      toast.error('Failed to escalate. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat Window */}
      {isOpen && (
        <Card className="mb-4 flex h-[500px] w-[380px] flex-col shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cloudflare-orange">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Partner Assistant</h3>
                <p className="text-xs text-muted-foreground">AI-powered support</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-2',
                  message.role === 'user' ? 'flex-row-reverse' : ''
                )}
              >
                <div className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  message.role === 'user' ? 'bg-muted' : 'bg-cloudflare-orange'
                )}>
                  {message.role === 'user' ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4 text-white" />
                  )}
                </div>
                <div className={cn(
                  'max-w-[80%] rounded-lg p-3 text-sm',
                  message.role === 'user' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted',
                  message.isEscalation && 'border-2 border-yellow-500'
                )}>
                  <p>{message.content}</p>
                  {message.sources && message.sources.length > 0 && (
                    <div className="mt-2 border-t pt-2">
                      <p className="text-xs text-muted-foreground">Sources:</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {message.sources.map((source, i) => (
                          <span key={i} className="text-xs bg-background/50 px-1 rounded">
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {message.isEscalation && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-yellow-600">
                      <Hand className="h-3 w-3" />
                      Escalated to human support
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cloudflare-orange">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-muted p-3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t p-4 space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Ask about certifications, support..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                className="flex-1"
              />
              <Button 
                size="icon" 
                onClick={sendMessage}
                disabled={isLoading || !input.trim()}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={escalateToHuman}
              disabled={isLoading}
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              Speak to a human
            </Button>
          </div>
        </Card>
      )}

      {/* Toggle Button */}
      <Button
        size="lg"
        className="h-14 w-14 rounded-full shadow-lg"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </Button>
    </div>
  )
}

import { toast } from 'sonner'
